import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Users, DollarSign, Phone, Activity, FlaskConical, CheckCircle2, XCircle, Trash2, Wallet, CreditCard, Brain } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";
import CoachTraining from "../components/CoachTraining";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const m = {
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ringing: "bg-amber-100 text-amber-800 border-amber-200",
    "no-answer": "bg-zinc-100 text-zinc-700 border-zinc-200",
    busy: "bg-zinc-100 text-zinc-700 border-zinc-200",
    failed: "bg-rose-100 text-rose-800 border-rose-200",
    canceled: "bg-zinc-100 text-zinc-700 border-zinc-200",
    no_agent: "bg-rose-100 text-rose-800 border-rose-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${m[status] || "bg-zinc-100 border-zinc-200"}`}>{status}</span>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [refundingId, setRefundingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, a, l, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/agents"),
        api.get("/admin/leads"),
        api.get("/admin/payments"),
      ]);
      setStats(s.data);
      setAgents(a.data.agents || []);
      setLeads(l.data.leads || []);
      setPayments(p.data.payments || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const refundPayment = async (p) => {
    const confirmMsg =
      `Refund $${Number(p.amount).toFixed(2)} back to ${p.agent_name || p.agent_email || "the agent"}'s card via Square?\n\n` +
      `This also deducts $${Number(p.amount).toFixed(2)} from their wallet balance to prevent double-crediting.`;
    if (!window.confirm(confirmMsg)) return;
    setRefundingId(p.id);
    try {
      const { data } = await api.post(`/admin/payments/${p.id}/refund`, { reason: "Admin refund" });
      toast.success(
        `Refunded $${Number(data.refund_amount).toFixed(2)} to card. ` +
        (data.wallet_delta ? `Wallet adjusted ${data.wallet_delta > 0 ? "+" : ""}$${Number(data.wallet_delta).toFixed(2)}.` : ""),
        { duration: 6000 },
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refund failed");
    } finally {
      setRefundingId(null);
    }
  };

  const approve = async (id, approved) => {
    try {
      await api.put(`/admin/agents/${id}/approve`, { approved });
      toast.success(approved ? "Agent approved" : "Approval revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this agent? Their lead history stays.")) return;
    try {
      await api.delete(`/admin/agents/${id}`);
      toast.success("Agent deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetStatus = async (id) => {
    try {
      await api.post(`/admin/agents/${id}/reset-status`);
      toast.success("Agent reset to READY");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setAgentStatus = async (id, status) => {
    try {
      await api.put(`/admin/agents/${id}/status`, { status });
      toast.success(
        status === "ready" ? "Agent activated" :
        status === "not_ready" ? "Agent paused" : "Agent set to offline"
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Admin Console</div>
          <h1 className="font-heading font-black text-4xl tracking-tight leading-none mt-1">Operations</h1>
        </div>
        <SimulateCallDialog onDone={load} agents={agents.filter((a) => a.approved)} />
      </div>

      {/* KPI Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
        <Kpi
          i={DollarSign}
          label="Topup revenue"
          value={`$${(stats?.topup_revenue || 0).toFixed(2)}`}
          sub={`${stats?.topup_count || 0} paid · cash collected`}
          mono
          testid="admin-kpi-topup-revenue"
        />
        <Kpi
          i={CreditCard}
          label="Lead revenue (billed)"
          value={`$${(stats?.lead_revenue || 0).toFixed(2)}`}
          sub="From agent wallets"
          mono
          testid="admin-kpi-revenue"
        />
        <Kpi i={Phone} label="Billable leads" value={stats?.billable_leads || 0} mono testid="admin-kpi-billable" />
        <Kpi i={Users} label="Approved agents" value={`${stats?.approved_agents || 0} / ${stats?.total_agents || 0}`} mono testid="admin-kpi-agents" />
        <Kpi i={Activity} label="Ready now" value={stats?.ready_agents || 0} mono testid="admin-kpi-ready" />
      </div>

      {/* Chart */}
      <div className="border border-t-0 border-zinc-200 bg-white p-6">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-4">Revenue — last 7 days</div>
        <div className="h-56" data-testid="admin-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats?.daily || []}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#71717a" />
              <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#71717a" />
              <ReTooltip
                contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
              />
              <Line type="monotone" dataKey="revenue" stroke="#0055FF" strokeWidth={2} dot={{ fill: "#0055FF", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Live in-call indicator */}
      {agents.filter((a) => a.status === "in_call").length > 0 && (
        <div className="border border-t-0 border-zinc-200 bg-zinc-950 text-white p-4" data-testid="admin-live-calls">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#10B981] ring-pulse" />
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Live now</span>
            <span className="font-mono font-bold ml-1">
              {agents.filter((a) => a.status === "in_call").length} agent(s) on calls
            </span>
            <span className="ml-auto text-xs text-zinc-400 font-mono">
              {agents.filter((a) => a.status === "in_call").map((a) => a.name).join(" · ")}
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="agents" className="mt-8">
        <TabsList className="btn-sharp">
          <TabsTrigger value="agents" className="btn-sharp text-xs uppercase tracking-widest" data-testid="admin-tab-agents">Agents</TabsTrigger>
          <TabsTrigger value="leads" className="btn-sharp text-xs uppercase tracking-widest" data-testid="admin-tab-leads">All leads</TabsTrigger>
          <TabsTrigger value="payments" className="btn-sharp text-xs uppercase tracking-widest" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="training" className="btn-sharp text-xs uppercase tracking-widest" data-testid="admin-tab-training">
            <Brain className="w-3.5 h-3.5 mr-1" /> AI Training
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="border border-zinc-200 bg-white mt-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Agent</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Phone</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Tier</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Wallet</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="admin-agents-table">
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-zinc-500 py-8 text-sm">No agents yet.</TableCell>
                </TableRow>
              )}
              {agents.map((a) => (
                <TableRow key={a.id} data-testid={`agent-row-${a.id}`}>
                  <TableCell>
                    <div className="font-bold">{a.name}</div>
                    <div className="text-xs text-zinc-500">{a.email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{a.phone || <span className="text-zinc-400">—</span>}</TableCell>
                  <TableCell className="font-mono">${a.buffer_tier === 75 ? 40 : 60} / {a.buffer_tier}s</TableCell>
                  <TableCell>
                    {a.approved ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Approved</Badge> : <Badge variant="outline">Pending</Badge>}
                    <span className="ml-1 text-[10px] uppercase tracking-widest text-zinc-500">{a.status}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">${a.wallet_balance.toFixed(2)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {!a.approved ? (
                      <Button size="sm" className="btn-sharp bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(a.id, true)} data-testid={`approve-agent-${a.id}-button`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="btn-sharp" onClick={() => approve(a.id, false)} data-testid={`revoke-agent-${a.id}-button`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                      </Button>
                    )}
                    <AdjustBalanceDialog agent={a} onDone={load} />
                    {a.status === "in_call" ? (
                      <Button size="sm" variant="outline" className="btn-sharp text-amber-700 border-amber-300" onClick={() => resetStatus(a.id)} data-testid={`reset-agent-${a.id}-button`}>
                        Reset
                      </Button>
                    ) : a.status === "ready" ? (
                      <Button size="sm" variant="outline" className="btn-sharp text-amber-700 border-amber-300" onClick={() => setAgentStatus(a.id, "not_ready")} data-testid={`pause-agent-${a.id}-button`}>
                        Pause
                      </Button>
                    ) : (
                      <Button size="sm" className="btn-sharp bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setAgentStatus(a.id, "ready")} data-testid={`activate-agent-${a.id}-button`}>
                        Activate
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="btn-sharp text-rose-600" onClick={() => remove(a.id)} data-testid={`delete-agent-${a.id}-button`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="leads" className="border border-zinc-200 bg-white mt-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">When</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Caller</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">State</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Agent</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Duration</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Buffer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Billed</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Recording</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="admin-leads-table">
              {leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-zinc-500 py-8 text-sm">No leads yet.</TableCell>
                </TableRow>
              )}
              {leads.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="font-mono">{l.customer_number}</TableCell>
                  <TableCell className="font-mono text-xs uppercase tracking-widest">{l.caller_state || <span className="text-zinc-400">—</span>}</TableCell>
                  <TableCell>{l.agent_name || <span className="text-zinc-400">unassigned</span>}</TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  <TableCell className="text-right font-mono">{l.duration}s</TableCell>
                  <TableCell className="text-right font-mono text-zinc-500">{l.buffer_seconds || "—"}s</TableCell>
                  <TableCell className="text-right font-mono font-bold">{l.billable ? `$${l.billed_amount.toFixed(2)}` : <span className="text-zinc-400">—</span>}</TableCell>
                  <TableCell>
                    {l.recording_url ? (
                      <audio
                        controls
                        preload="none"
                        src={`${API_BASE}/leads/${l.id}/recording.mp3`}
                        className="h-8 max-w-[220px]"
                        data-testid={`admin-lead-recording-${l.id}`}
                      >
                        <track kind="captions" />
                      </audio>
                    ) : (
                      <span className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="payments" className="border border-zinc-200 bg-white mt-2">
          <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border-b border-zinc-200">
            {(stats?.topup_by_type || []).length === 0 ? (
              <div className="bg-white p-6 sm:col-span-3 text-sm text-zinc-500 font-mono uppercase tracking-widest">
                No paid transactions yet.
              </div>
            ) : (
              stats.topup_by_type.map((t) => (
                <div key={t.type} className="bg-white p-6" data-testid={`admin-topup-type-${t.type}`}>
                  <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">
                    {t.type === "auto_refill" ? "Auto-refills" : "Manual top-ups"}
                  </div>
                  <div className="mt-2 font-mono font-black text-3xl tracking-tight">
                    ${t.total.toFixed(2)}
                  </div>
                  <div className="mt-1 text-[10px] font-mono tracking-widest uppercase text-zinc-500">
                    {t.count} transaction{t.count === 1 ? "" : "s"}
                  </div>
                </div>
              ))
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">When</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Agent</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Type</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold">Square Payment ID</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Amount</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="admin-payments-table">
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-zinc-500 py-8 text-sm">
                    No payments yet. Top-ups and auto-refills will appear here once agents purchase credits.
                  </TableCell>
                </TableRow>
              )}
              {payments.map((p) => {
                const canRefund = p.payment_status === "paid" && !!p.square_payment_id;
                return (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">
                    {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="font-bold text-sm">{p.agent_name || <span className="text-zinc-400">Unknown</span>}</div>
                    <div className="text-xs text-zinc-500 font-mono">{p.agent_email || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                      p.type === "auto_refill"
                        ? "bg-blue-50 text-blue-800 border-blue-200"
                        : "bg-zinc-100 text-zinc-700 border-zinc-200"
                    }`}>
                      {p.type === "auto_refill" ? "Auto-refill" : "Top-up"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                      p.payment_status === "paid"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : p.payment_status === "refunded"
                        ? "bg-zinc-200 text-zinc-800 border-zinc-300"
                        : p.payment_status === "partially_refunded"
                        ? "bg-amber-100 text-amber-800 border-amber-200"
                        : p.payment_status === "pending"
                        ? "bg-amber-100 text-amber-800 border-amber-200"
                        : "bg-rose-100 text-rose-800 border-rose-200"
                    }`}>
                      {p.payment_status || p.status || "—"}
                    </span>
                    {p.refunded_amount > 0 && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        Refunded ${Number(p.refunded_amount).toFixed(2)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-zinc-500">
                    {p.square_payment_id || <span className="text-zinc-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">
                    ${Number(p.amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canRefund ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-sharp text-xs uppercase tracking-widest border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => refundPayment(p)}
                        disabled={refundingId === p.id}
                        data-testid={`refund-payment-${p.id}`}
                      >
                        {refundingId === p.id ? "Refunding…" : "Refund to card"}
                      </Button>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="training" className="mt-2">
          <CoachTraining />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ i: Icon, label, value, mono, testid, sub }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`mt-2 ${mono ? "font-mono" : "font-heading"} font-black text-3xl tracking-tight`}>{value}</div>
      {sub && <div className="mt-1 text-[10px] font-mono tracking-widest uppercase text-zinc-500">{sub}</div>}
    </div>
  );
}

function AdjustBalanceDialog({ agent, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/agents/${agent.id}/adjustments`);
      setHistory(data.adjustments || []);
    } catch (err) {
      console.error("Failed to load adjustment history:", err);
    }
  }, [agent.id]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const submit = async (sign) => {
    const value = parseFloat(amount);
    if (!value || Number.isNaN(value) || value <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/agents/${agent.id}/adjust-balance`, {
        amount: sign * value,
        note: note.trim(),
      });
      toast.success(`${sign > 0 ? "Credited" : "Debited"} $${value.toFixed(2)} ${sign > 0 ? "to" : "from"} ${agent.name}`);
      setAmount("");
      setNote("");
      onDone();
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="btn-sharp" data-testid={`adjust-balance-${agent.id}-button`}>
          <Wallet className="w-3.5 h-3.5 mr-1" /> Adjust $
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tight">
            Adjust wallet — {agent.name}
          </DialogTitle>
        </DialogHeader>
        <div className="border border-zinc-200 p-4 bg-zinc-50">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Current balance</div>
          <div className="font-mono font-black text-3xl mt-1" data-testid={`adjust-balance-${agent.id}-current`}>
            ${agent.wallet_balance.toFixed(2)}
          </div>
        </div>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600">Amount (USD)</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100.00"
              className="btn-sharp font-mono"
              data-testid={`adjust-balance-${agent.id}-amount`}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Promo credit, refund, manual top-up"
              className="btn-sharp"
              data-testid={`adjust-balance-${agent.id}-note`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              onClick={() => submit(1)}
              disabled={busy}
              className="btn-sharp bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider text-xs"
              data-testid={`adjust-balance-${agent.id}-credit`}
            >
              + Credit wallet
            </Button>
            <Button
              onClick={() => submit(-1)}
              disabled={busy}
              variant="outline"
              className="btn-sharp font-bold uppercase tracking-wider text-xs"
              data-testid={`adjust-balance-${agent.id}-debit`}
            >
              − Debit wallet
            </Button>
          </div>
        </div>
        {history.length > 0 && (
          <div className="mt-4 border-t border-zinc-200 pt-4">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
              Recent adjustments
            </div>
            <div className="max-h-48 overflow-auto divide-y divide-zinc-200">
              {history.map((h) => (
                <div key={h.id} className="py-2 flex items-start justify-between text-sm">
                  <div className="flex-1">
                    <div className={`font-mono font-bold ${h.amount > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {h.amount > 0 ? "+" : ""}${h.amount.toFixed(2)}
                    </div>
                    {h.note && <div className="text-xs text-zinc-600 mt-0.5">{h.note}</div>}
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">
                      by {h.admin_email} · {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                    </div>
                  </div>
                  <div className="font-mono text-xs text-zinc-500">→ ${h.balance_after?.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function SimulateCallDialog({ onDone, agents }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("+14075550142");
  const [agentId, setAgentId] = useState("");
  const [duration, setDuration] = useState(90);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/twilio/simulate", {
        customer_number: from,
        agent_id: agentId || null,
        duration: Number(duration),
      });
      toast.success("Simulated lead created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="btn-sharp text-xs uppercase tracking-widest font-bold" data-testid="admin-simulate-call-button">
          <FlaskConical className="w-3.5 h-3.5 mr-1" /> Simulate inbound
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tight">Simulate inbound call</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600">Caller number</Label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} className="btn-sharp font-mono" data-testid="sim-caller-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600">Route to agent (optional)</Label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full mt-1 border border-zinc-300 px-3 h-10 text-sm"
              data-testid="sim-agent-select"
            >
              <option value="">— Any ready agent —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.phone || "no phone"}) · {a.buffer_tier}s</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-600">Call duration (seconds)</Label>
            <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="btn-sharp font-mono" data-testid="sim-duration-input" />
            <div className="text-[11px] text-zinc-500 mt-1">Goes billable if it exceeds the agent's buffer (75 or 115s).</div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full btn-sharp bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wider text-xs" data-testid="sim-submit-button">
            {busy ? "Simulating…" : "Fire simulated call"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
